import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX, HiOutlineTrash } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import ConfirmDialog from "components/ConfirmDialog";
import {
  ASSIGNMENT_ADDRESSEE_DESIGNATIONS,
  ASSIGNMENT_APPLICANT_TYPES,
  ASSIGNMENT_CLASS_NUMBERS,
  ASSIGNMENT_FORM_OF_ADDRESS_OPTIONS,
  ASSIGNMENT_PLACE_OPTIONS,
  ASSIGNMENT_TYPE_OF_MARK_OPTIONS,
  generateAssignmentUploadPresignedUrls,
  getLeadPrefill,
  submitAssignmentApplicationDetails,
  type AssignmentInvoiceApplicationInput,
  type SubmitAssignmentApplicationDocumentInput,
} from "../../api/assignment";
import { useAssignmentDiscussion } from "./AssignmentDiscussionContext";

export const ASSIGNMENT_APPLICATION_DETAILS_FORM_ID =
  "assignment-application-details-form";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp,.pdf";
const ACCEPTED_HELP = "JPEG, PNG, PDF formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const FILE_INPUT_PREFIX = "assignment-application-details-file-input-";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const REQ = <span className="text-red-500 ml-0.5">*</span>;
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "w-full min-h-[64px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border resize-y";

type SavedDocument = {
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
};

type EditableApplication = {
  rowId: string;
  tmNo: string;
  dateOfFiling: Date | null;
  mark: string;
  typeOfMark: string;
  classNumber: string;
  validityDate: Date | null;
  pendingFiles: File[];
  savedDocuments: SavedDocument[];
};

let rowIdCounter = 0;
function makeRowId(): string {
  rowIdCounter += 1;
  return `app-row-${rowIdCounter}-${Date.now()}`;
}

function makeEmptyApplication(defaultMark: string = ""): EditableApplication {
  return {
    rowId: makeRowId(),
    tmNo: "",
    dateOfFiling: null,
    mark: defaultMark,
    typeOfMark: "",
    classNumber: "",
    validityDate: null,
    pendingFiles: [],
    savedDocuments: [],
  };
}

function parseDateInput(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function AssignmentApplicationDetailsFromClientView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  // Addressee
  const [addresseeDesignation, setAddresseeDesignation] = useState("");
  const [addresseeCustomName, setAddresseeCustomName] = useState("");
  const [formOfAddress, setFormOfAddress] = useState("");
  const [firmName, setFirmName] = useState("");
  const [place, setPlace] = useState("");
  const [placeManualMode, setPlaceManualMode] = useState(false);

  // Applicant block
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [dateOfPowerOfAttorney, setDateOfPowerOfAttorney] = useState<Date | null>(null);
  const [descriptionOfApplicant, setDescriptionOfApplicant] = useState("");

  // Applications (with embedded per-app uploads)
  const [applications, setApplications] = useState<EditableApplication[]>([
    makeEmptyApplication(),
  ]);
  const [preliminaryMark, setPreliminaryMark] = useState<string>("");
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | null>(null);

  // Common (lead-level) uploads — not tied to any specific application.
  const [commonPendingFiles, setCommonPendingFiles] = useState<File[]>([]);
  const [commonSavedDocuments, setCommonSavedDocuments] = useState<SavedDocument[]>([]);
  const commonFileInputId = "assignment-application-details-common-file-input";

  // Inline validation: keys are top-level field names, or `app:{rowId}:{field}`.
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const hasError = (key: string) => errors.has(key);
  const errorBorder = (key: string) => (hasError(key) ? " !border-red-500" : "");
  const clearError = (key: string) => {
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const prefilledRef = useRef(false);
  const populatedForLeadIdRef = useRef<string | null>(null);

  const { details, loading: detailsLoading, error: detailsError, refresh } =
    useAssignmentDiscussion();

  useEffect(() => {
    if (leadId !== populatedForLeadIdRef.current) {
      populatedForLeadIdRef.current = null;
      prefilledRef.current = false;
    }

    if (!leadId) {
      setLoading(false);
      return;
    }
    if (detailsLoading) {
      setLoading(true);
      return;
    }
    if (detailsError) {
      setLoading(false);
      toast.error(detailsError.message || "Failed to load application details");
      return;
    }
    if (!details) return;
    if (populatedForLeadIdRef.current === leadId) {
      setLoading(false);
      return;
    }

    populatedForLeadIdRef.current = leadId;
    let cancelled = false;

    const prelimMark = details.preliminary?.markName ?? "";
    setPreliminaryMark(prelimMark);

    const d = details.applicationDetails;
    if (d) {
      setAddresseeDesignation(d.addresseeDesignation ?? "");
      setAddresseeCustomName(d.addresseeCustomName ?? "");
      setFormOfAddress(d.formOfAddress ?? "");
      setFirmName(d.firmName ?? "");
      setPlace(d.place ?? "");
      setApplicant(d.applicant ?? "");
      setApplicantAddress(d.applicantAddress ?? "");
      setApplicantType(d.applicantType ?? "");
      setSignatoryName(d.signatoryName ?? "");
      setSignatoryDesignation(d.signatoryDesignation ?? "");
      setDateOfPowerOfAttorney(parseDateInput(d.dateOfPowerOfAttorney));
      setDescriptionOfApplicant(d.descriptionOfApplicant ?? "");
    }

    // Split saved documents: rows with `applicationOrdinal != null` belong to
    // a specific application; rows with null go into the common (lead-level)
    // bucket.
    const savedDocs = details.documents ?? [];
    const docsByOrdinal = new Map<number, SavedDocument[]>();
    const commonDocs: SavedDocument[] = [];
    for (const doc of savedDocs) {
      const saved: SavedDocument = {
        fileKey: doc.fileKey,
        fileName: doc.fileName,
        fileUrl: doc.fileUrl ?? null,
      };
      if (doc.applicationOrdinal != null && doc.applicationOrdinal >= 0) {
        const list = docsByOrdinal.get(doc.applicationOrdinal) ?? [];
        list.push(saved);
        docsByOrdinal.set(doc.applicationOrdinal, list);
      } else {
        commonDocs.push(saved);
      }
    }
    setCommonSavedDocuments(commonDocs);

    if (details.applications.length > 0) {
      setApplications(
        details.applications.map((a, idx) => ({
          rowId: makeRowId(),
          tmNo: a.tmNo ?? "",
          dateOfFiling: parseDateInput(a.dateOfFiling),
          mark: prelimMark || a.mark || "",
          typeOfMark: a.typeOfMark ?? "",
          classNumber: a.classNumber != null ? String(a.classNumber) : "",
          validityDate: parseDateInput(a.validityDate),
          pendingFiles: [],
          savedDocuments: docsByOrdinal.get(idx) ?? [],
        }))
      );
    } else if (prelimMark || savedDocs.length > 0) {
      const first = makeEmptyApplication(prelimMark);
      first.savedDocuments = docsByOrdinal.get(0) ?? [];
      setApplications([first]);
    }

    // Prefill from reference lead when nothing has been saved yet.
    const hasSavedAppDetails = Boolean(d && (d.applicant || d.applicantAddress));
    const hasSavedApplications = details.applications.length > 0;
    if (
      !prefilledRef.current &&
      details.referenceLeadId &&
      !hasSavedAppDetails
    ) {
      prefilledRef.current = true;
      (async () => {
        try {
          const prefill = await getLeadPrefill(details.referenceLeadId!);
          if (cancelled) return;
          if (prefill.applicant) setApplicant(prefill.applicant);
          if (prefill.applicantAddress) setApplicantAddress(prefill.applicantAddress);
          if (prefill.applicantType) setApplicantType(prefill.applicantType);
          if (prefill.signatoryName) setSignatoryName(prefill.signatoryName);
          if (prefill.signatoryDesignation)
            setSignatoryDesignation(prefill.signatoryDesignation);
          if (prefill.descriptionOfApplicant)
            setDescriptionOfApplicant(prefill.descriptionOfApplicant);
          const poaDate = parseDateInput(prefill.dateOfPowerOfAttorney);
          if (poaDate) setDateOfPowerOfAttorney(poaDate);
          if (prefill.addressee?.formOfAddress)
            setFormOfAddress(prefill.addressee.formOfAddress);
          if (prefill.addressee?.districtOfApplicant)
            setPlace(prefill.addressee.districtOfApplicant);
          const line1 = prefill.addressee?.addresseeLine1?.trim();
          if (line1) {
            if ((ASSIGNMENT_ADDRESSEE_DESIGNATIONS as readonly string[]).includes(line1)) {
              setAddresseeDesignation(line1);
            } else {
              setAddresseeDesignation("NAME");
              setAddresseeCustomName(line1);
            }
          }
          const line2 = prefill.addressee?.addresseeLine2?.trim();
          if (line2) setFirmName(line2);
          if (!hasSavedApplications && Array.isArray(prefill.applications) && prefill.applications.length > 0) {
            setApplications(
              prefill.applications.map<EditableApplication>((a) => ({
                rowId: makeRowId(),
                tmNo: a.tmNo ?? "",
                dateOfFiling: parseDateInput(a.applDate),
                mark: prelimMark || a.mark || "",
                typeOfMark: a.typeOfMark ?? "",
                classNumber: a.classNumber != null ? String(a.classNumber) : "",
                validityDate: parseDateInput(a.validityDate),
                pendingFiles: [],
                savedDocuments: [],
              }))
            );
          }
        } catch {
          // prefill is best-effort; ignore errors
        }
      })();
    }

    setLoading(false);

    return () => {
      cancelled = true;
    };
  }, [leadId, details, detailsLoading, detailsError]);

  const updateApplication = (rowId: string, patch: Partial<EditableApplication>) => {
    setApplications((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
    for (const field of Object.keys(patch)) {
      clearError(`app:${rowId}:${field}`);
    }
  };

  const onFilesSelectedForApp = (rowId: string, files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const accepted: File[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setApplications((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? { ...row, pendingFiles: [...row.pendingFiles, ...accepted] }
          : row
      )
    );
    clearError(`app:${rowId}:files`);
  };

  const removePendingFile = (rowId: string, idx: number) =>
    setApplications((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              pendingFiles: row.pendingFiles.filter((_, i) => i !== idx),
            }
          : row
      )
    );

  const removeSavedDocument = (rowId: string, idx: number) =>
    setApplications((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              savedDocuments: row.savedDocuments.filter((_, i) => i !== idx),
            }
          : row
      )
    );

  const onCommonFilesSelected = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const accepted: File[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setCommonPendingFiles((prev) => [...prev, ...accepted]);
  };

  const removeCommonPendingFile = (idx: number) =>
    setCommonPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeCommonSavedDocument = (idx: number) =>
    setCommonSavedDocuments((prev) => prev.filter((_, i) => i !== idx));

  const openPendingPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    setPreview({
      url,
      name: file.name,
      kind: detectPreviewKind(file.name, file.type),
      revokeOnClose: true,
    });
  };

  const openSavedPreview = (doc: SavedDocument) => {
    if (!doc.fileUrl) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({
      url: doc.fileUrl,
      name: doc.fileName,
      kind: detectPreviewKind(doc.fileName),
    });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // --- Applications row helpers ---

  const addApplicationRow = () => {
    setApplications((prev) => [...prev, makeEmptyApplication(preliminaryMark)]);
  };

  const removeApplicationRow = (rowId: string) => {
    setApplications((prev) => {
      const next = prev.filter((row) => row.rowId !== rowId);
      return next.length === 0 ? [makeEmptyApplication(preliminaryMark)] : next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const next = new Set<string>();
    if (!addresseeDesignation) next.add("addresseeDesignation");
    if (addresseeDesignation === "NAME" && !addresseeCustomName.trim())
      next.add("addresseeCustomName");
    if (!formOfAddress) next.add("formOfAddress");
    if (!firmName.trim()) next.add("firmName");
    if (!place.trim()) next.add("place");
    if (!applicant.trim()) next.add("applicant");
    if (!applicantType) next.add("applicantType");
    if (!applicantAddress.trim()) next.add("applicantAddress");
    if (!signatoryName.trim()) next.add("signatoryName");
    if (!signatoryDesignation.trim()) next.add("signatoryDesignation");
    if (!descriptionOfApplicant.trim()) next.add("descriptionOfApplicant");

    for (const row of applications) {
      if (!row.tmNo.trim()) next.add(`app:${row.rowId}:tmNo`);
      if (!row.dateOfFiling) next.add(`app:${row.rowId}:dateOfFiling`);
      if (!row.mark.trim()) next.add(`app:${row.rowId}:mark`);
      if (!row.typeOfMark) next.add(`app:${row.rowId}:typeOfMark`);
      if (!row.classNumber) next.add(`app:${row.rowId}:classNumber`);
      if (!row.validityDate) next.add(`app:${row.rowId}:validityDate`);
    }

    if (next.size > 0) {
      setErrors(next);
      return;
    }
    setErrors(new Set());

    setSaving(true);
    try {
      // Flatten pending files across all applications + common bucket. `appIdx`
      // is the application's index in `applications[]`, or null for common docs.
      const flatPending: Array<{ file: File; appIdx: number | null }> = [];
      applications.forEach((row, idx) => {
        for (const file of row.pendingFiles) {
          flatPending.push({ file, appIdx: idx });
        }
      });
      for (const file of commonPendingFiles) {
        flatPending.push({ file, appIdx: null });
      }

      let uploadedDocs: Array<{
        fileKey: string;
        fileName: string;
        appIdx: number | null;
      }> = [];
      if (flatPending.length > 0) {
        const presigned = await generateAssignmentUploadPresignedUrls(
          leadId,
          flatPending.map(({ file }) => ({
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
          }))
        );
        if (presigned.length !== flatPending.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          flatPending.map(async ({ file }, i) => {
            const target = presigned[i];
            const putRes = await fetch(target.url, {
              method: "PUT",
              body: file,
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
            });
            if (!putRes.ok) throw new Error(`Failed to upload ${file.name}`);
          })
        );
        uploadedDocs = flatPending.map(({ file, appIdx }, i) => ({
          fileKey: presigned[i].key,
          fileName: file.name,
          appIdx,
        }));
      }

      // Build the final documents list: per-app saved + common saved + newly
      // uploaded docs. Per-app rows carry `applicationOrdinal`; common rows omit
      // it (backend stores null).
      const documents: SubmitAssignmentApplicationDocumentInput[] = [];
      applications.forEach((row, idx) => {
        for (const doc of row.savedDocuments) {
          documents.push({
            fileKey: doc.fileKey,
            fileName: doc.fileName,
            applicationOrdinal: idx,
          });
        }
      });
      for (const doc of commonSavedDocuments) {
        documents.push({ fileKey: doc.fileKey, fileName: doc.fileName });
      }
      for (const up of uploadedDocs) {
        documents.push({
          fileKey: up.fileKey,
          fileName: up.fileName,
          ...(up.appIdx != null ? { applicationOrdinal: up.appIdx } : {}),
        });
      }

      await submitAssignmentApplicationDetails(leadId, {
        documents,
        addresseeDesignation: addresseeDesignation || undefined,
        addresseeCustomName:
          addresseeDesignation === "NAME" && addresseeCustomName
            ? addresseeCustomName
            : undefined,
        formOfAddress: formOfAddress || undefined,
        firmName: firmName.trim() || undefined,
        place: place.trim() || undefined,
        applicant: applicant.trim() || undefined,
        applicantAddress: applicantAddress.trim() || undefined,
        applicantType: applicantType || undefined,
        signatoryName: signatoryName.trim() || undefined,
        signatoryDesignation: signatoryDesignation.trim() || undefined,
        dateOfPowerOfAttorney:
          toISODateString(dateOfPowerOfAttorney) ?? undefined,
        descriptionOfApplicant: descriptionOfApplicant.trim() || undefined,
        applications: applications
          .filter((row) => row.tmNo.trim().length > 0)
          .map<AssignmentInvoiceApplicationInput>((row) => ({
            tmNo: row.tmNo.trim(),
            dateOfFiling: toISODateString(row.dateOfFiling),
            mark: row.mark.trim() || null,
            typeOfMark: row.typeOfMark || null,
            classNumber:
              row.classNumber && !isNaN(Number(row.classNumber))
                ? Number(row.classNumber)
                : null,
            validityDate: toISODateString(row.validityDate),
          })),
      });

      // Move newly uploaded files into savedDocuments for their respective apps.
      if (uploadedDocs.length > 0) {
        setApplications((prev) =>
          prev.map((row, idx) => {
            const mine = uploadedDocs.filter((u) => u.appIdx === idx);
            if (mine.length === 0 && row.pendingFiles.length === 0) return row;
            return {
              ...row,
              pendingFiles: [],
              savedDocuments: [
                ...row.savedDocuments,
                ...mine.map((u) => ({
                  fileKey: u.fileKey,
                  fileName: u.fileName,
                  fileUrl: null,
                })),
              ],
            };
          })
        );
        const newCommon = uploadedDocs
          .filter((u) => u.appIdx === null)
          .map((u) => ({
            fileKey: u.fileKey,
            fileName: u.fileName,
            fileUrl: null as string | null,
          }));
        if (newCommon.length > 0 || commonPendingFiles.length > 0) {
          setCommonSavedDocuments((prev) => [...prev, ...newCommon]);
          setCommonPendingFiles([]);
        }
      }

      await refresh();
      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save application details"
      );
    } finally {
      setSaving(false);
    }
  };

  const showAddresseeCustomName = addresseeDesignation === "NAME";

  const placeIsManual = useMemo(
    () =>
      placeManualMode ||
      (place !== "" &&
        !ASSIGNMENT_PLACE_OPTIONS.includes(place as (typeof ASSIGNMENT_PLACE_OPTIONS)[number])),
    [place, placeManualMode]
  );

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this
          step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={ASSIGNMENT_APPLICATION_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* --- Addressee --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Addressee</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Addressee Designation{REQ}</label>
            <Select
              value={addresseeDesignation}
              onValueChange={(v) => {
                setAddresseeDesignation(v);
                clearError("addresseeDesignation");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("addresseeDesignation")}>
                <SelectValue placeholder="Select Designation" />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNMENT_ADDRESSEE_DESIGNATIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showAddresseeCustomName && (
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Name{REQ}</label>
              <input
                type="text"
                value={addresseeCustomName}
                onChange={(e) => {
                  setAddresseeCustomName(e.target.value);
                  clearError("addresseeCustomName");
                }}
                placeholder="Enter name"
                className={INPUT_CLASS + errorBorder("addresseeCustomName")}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Form of Address{REQ}</label>
            <Select
              value={formOfAddress}
              onValueChange={(v) => {
                setFormOfAddress(v);
                clearError("formOfAddress");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("formOfAddress")}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNMENT_FORM_OF_ADDRESS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Firm Name{REQ}</label>
            <input
              type="text"
              value={firmName}
              onChange={(e) => {
                setFirmName(e.target.value);
                clearError("firmName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("firmName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className={LABEL_CLASS}>Place{REQ}</label>
              {placeIsManual && (
                <button
                  type="button"
                  onClick={() => {
                    setPlaceManualMode(false);
                    setPlace("");
                    clearError("place");
                  }}
                  className="text-[11px] font-normal text-[#0070F0] hover:underline"
                >
                  Choose from list
                </button>
              )}
            </div>
            {placeIsManual ? (
              <input
                type="text"
                value={place}
                onChange={(e) => {
                  setPlace(e.target.value);
                  clearError("place");
                }}
                placeholder="Enter place"
                className={INPUT_CLASS + errorBorder("place")}
                autoFocus={placeManualMode && !place}
              />
            ) : (
              <Select
                value={place}
                onValueChange={(v) => {
                  if (v === "__OTHER__") {
                    setPlaceManualMode(true);
                    setPlace("");
                    clearError("place");
                    return;
                  }
                  setPlace(v);
                  clearError("place");
                }}
              >
                <SelectTrigger className={INPUT_CLASS + errorBorder("place")}>
                  <SelectValue placeholder="Select Place" />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_PLACE_OPTIONS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                  <SelectItem value="__OTHER__">Other (Enter manually)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* --- Applicant --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Applicant Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant{REQ}</label>
            <input
              type="text"
              value={applicant}
              onChange={(e) => {
                setApplicant(e.target.value);
                clearError("applicant");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("applicant")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Type{REQ}</label>
            <Select
              value={applicantType}
              onValueChange={(v) => {
                setApplicantType(v);
                clearError("applicantType");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("applicantType")}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNMENT_APPLICANT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1 md:col-span-2">
            <label className={LABEL_CLASS}>Applicant Address{REQ}</label>
            <textarea
              value={applicantAddress}
              onChange={(e) => {
                setApplicantAddress(e.target.value);
                clearError("applicantAddress");
              }}
              placeholder="Address"
              className={TEXTAREA_CLASS + errorBorder("applicantAddress")}
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory's Name{REQ}</label>
            <input
              type="text"
              value={signatoryName}
              onChange={(e) => {
                setSignatoryName(e.target.value);
                clearError("signatoryName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("signatoryName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory's Designation{REQ}</label>
            <input
              type="text"
              value={signatoryDesignation}
              onChange={(e) => {
                setSignatoryDesignation(e.target.value);
                clearError("signatoryDesignation");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("signatoryDesignation")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Date of Power of Attorney</label>
            <DateTimePicker
              value={dateOfPowerOfAttorney}
              onChange={(date) => setDateOfPowerOfAttorney(date)}
              placeholder="Select Date"
              buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
            />
          </div>

          <div className="flex flex-col gap-1 md:col-span-2">
            <label className={LABEL_CLASS}>Description of Applicant{REQ}</label>
            <textarea
              value={descriptionOfApplicant}
              onChange={(e) => {
                setDescriptionOfApplicant(e.target.value);
                clearError("descriptionOfApplicant");
              }}
              placeholder="Description"
              className={TEXTAREA_CLASS + errorBorder("descriptionOfApplicant")}
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* --- Common uploads (not tied to any specific application) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            Supporting Documents
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (saving) return;
              document.getElementById(commonFileInputId)?.click();
            }}
            className="h-9 px-3 inline-flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] text-[12px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)" }}
          >
            Select Files
          </button>
        </div>
        <p className="text-[11px] text-[#6D6D6D] -mt-3">{ACCEPTED_HELP}</p>

        <input
          id={commonFileInputId}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onCommonFilesSelected(e.target.files);
            }
            e.currentTarget.value = "";
          }}
        />

        {(commonSavedDocuments.length > 0 || commonPendingFiles.length > 0) && (
          <div className="flex flex-col gap-2">
            {commonSavedDocuments.map((doc, idx) => (
              <div
                key={`common-saved-${doc.fileKey}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                  {doc.fileName}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openSavedPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  {doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName}
                      className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </a>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${doc.fileName}`}
                    onClick={() => removeCommonSavedDocument(idx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {commonPendingFiles.map((file, idx) => (
              <div
                key={`common-pending-${idx}-${file.name}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
              >
                <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                  {file.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openPendingPreview(file)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeCommonPendingFile(idx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Applications (with embedded per-app uploads) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            TM Applications
          </p>
          <button
            type="button"
            onClick={addApplicationRow}
            className="text-sm font-normal text-[#0070F0] hover:underline"
          >
            + Add Application
          </button>
        </div>

        <div>
          <div className="flex flex-col">
            <div className="grid grid-cols-[130px_130px_105px_85px_130px_minmax(0,1fr)_28px] gap-3 px-1 pb-2">
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Application No.{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Date of Filing{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Type of Mark{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Class Number{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Validity Date{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Upload File</div>
              <div />
            </div>

            {applications.map((row) => {
              const fileInputId = `${FILE_INPUT_PREFIX}${row.rowId}`;
              const appFileCount = row.pendingFiles.length + row.savedDocuments.length;
              return (
                <div
                  key={row.rowId}
                  className="flex flex-col gap-2 py-3 border-t border-[#EEEEEE] first:border-t-0"
                >
                  <div className="grid grid-cols-[130px_130px_105px_85px_130px_minmax(0,1fr)_28px] gap-3 px-1 items-start">
                    <input
                      type="text"
                      value={row.tmNo}
                      onChange={(e) => updateApplication(row.rowId, { tmNo: e.target.value })}
                      placeholder="e.g. 1234567"
                      className={INPUT_CLASS + errorBorder(`app:${row.rowId}:tmNo`)}
                    />
                    <DateTimePicker
                      value={row.dateOfFiling}
                      onChange={(date) => updateApplication(row.rowId, { dateOfFiling: date })}
                      placeholder="Select Date"
                      buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:dateOfFiling`)}
                    />
                    <Select
                      value={row.typeOfMark}
                      onValueChange={(v) => updateApplication(row.rowId, { typeOfMark: v })}
                    >
                      <SelectTrigger className={INPUT_CLASS + errorBorder(`app:${row.rowId}:typeOfMark`)}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNMENT_TYPE_OF_MARK_OPTIONS.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={row.classNumber}
                      onValueChange={(v) => updateApplication(row.rowId, { classNumber: v })}
                    >
                      <SelectTrigger className={INPUT_CLASS + errorBorder(`app:${row.rowId}:classNumber`)}>
                        <SelectValue placeholder="Select Class" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNMENT_CLASS_NUMBERS.map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <DateTimePicker
                      value={row.validityDate}
                      onChange={(date) => updateApplication(row.rowId, { validityDate: date })}
                      placeholder="Select Date"
                      buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:validityDate`)}
                    />
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => {
                        if (saving) return;
                        document.getElementById(fileInputId)?.click();
                      }}
                      className="h-9 w-full flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "14px",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Select File
                    </button>
                    <div className="flex items-center justify-center h-9">
                      {applications.length > 1 ? (
                        <button
                          type="button"
                          aria-label="Remove application"
                          onClick={() => setPendingDeleteRowId(row.rowId)}
                          className="text-[#6D6D6D] hover:text-red-600 p-1"
                        >
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <input
                    id={fileInputId}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        onFilesSelectedForApp(row.rowId, e.target.files);
                      }
                      e.currentTarget.value = "";
                    }}
                  />

                  {appFileCount > 0 && (
                    <div className="grid grid-cols-[130px_130px_105px_85px_130px_minmax(0,1fr)_28px] gap-3 px-1">
                      <div className="col-start-6 col-span-1 flex flex-col gap-2">
                        {row.savedDocuments.map((doc, dIdx) => (
                          <div
                            key={`saved-${doc.fileKey}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
                          >
                            <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                              {doc.fileName}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => openSavedPreview(doc)}
                                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                              >
                                Preview
                              </button>
                              {doc.fileUrl ? (
                                <a
                                  href={doc.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={doc.fileName}
                                  className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                                >
                                  Download
                                </a>
                              ) : null}
                              <button
                                type="button"
                                aria-label={`Remove ${doc.fileName}`}
                                onClick={() => removeSavedDocument(row.rowId, dIdx)}
                                disabled={saving}
                                className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                              >
                                <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                              </button>
                            </div>
                          </div>
                        ))}
                        {row.pendingFiles.map((file, pIdx) => (
                          <div
                            key={`pending-${pIdx}-${file.name}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
                          >
                            <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                              {file.name}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => openPendingPreview(file)}
                                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                aria-label={`Remove ${file.name}`}
                                onClick={() => removePendingFile(row.rowId, pIdx)}
                                disabled={saving}
                                className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                              >
                                <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />

      <ConfirmDialog
        open={!!pendingDeleteRowId}
        title="Delete Application"
        description="Are you sure you want to delete this application? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (pendingDeleteRowId) removeApplicationRow(pendingDeleteRowId);
          setPendingDeleteRowId(null);
        }}
        onClose={() => setPendingDeleteRowId(null)}
      />
    </form>
  );
}
